import { ref, computed, type Ref } from 'vue'
import type { DropdownItem } from '@designSystem/components'
import { useScrollIntoView } from '@/features/interface/composables/useScrollIntoView'
import { useMenuPositioning, type MenuDirection } from '@designSystem/composables'

export type DropdownDirection = MenuDirection

export function useDropdownMenuHandler<T = DropdownItem>(
  items: T[] = [],
  keyId?: keyof T,
  keyLabel?: keyof T,
  keyIconName?: keyof T,
  dropdownRef?: Ref<HTMLElement | null>,
  externalIsOpen?: Ref<boolean>,
) {
  const isOpen = externalIsOpen ?? ref(false)

  const { makeId, makeVisible } = useScrollIntoView()

  const {
    menuDirection: dropdownDirection,
    menuStyle: dropdownMenuStyle,
    updateMenuPosition: updateDropdownVisibilityAndDirection,
  } = useMenuPositioning({
    anchorRef: dropdownRef ?? ref(null),
    isOpen,
    minBottomSpace: 300,
  })

  const computedItems = computed<DropdownItem[]>(() =>
    items.map((item: T) => ({
      id: item[(keyId as keyof T) ?? ('id' as keyof T)] as DropdownItem['id'],
      label: item[(keyLabel as keyof T) ?? ('label' as keyof T)] as DropdownItem['label'],
      iconName: item[(keyIconName as keyof T) ?? ('iconName' as keyof T)] as DropdownItem['iconName'],
      disabled: (item as Record<string, unknown>)['disabled'] as DropdownItem['disabled'],
      disabledReason: (item as Record<string, unknown>)['disabledReason'] as DropdownItem['disabledReason'],
    })),
  )

  return {
    isOpen,
    dropdownDirection,
    dropdownMenuStyle,
    computedItems,
    updateDropdownVisibilityAndDirection,
    makeId,
    makeVisible,
  }
}

